import type { DocRef, DocCategory } from '@nandanam/domain';
import { downloadLink, uploadFile, type BusinessLocation, type Graph, type OneDriveRemote } from '@nandanam/sync';

/**
 * Where uploaded documents live. Real mode stores them in the business folder in OneDrive
 * (documents/, evidence/, receipts/). Demo mode keeps them in this browser's IndexedDB only.
 */
export interface FileStore {
  put(file: File, opts: { id: string; folder: string; cat: DocCategory; date: string }): Promise<DocRef>;
  url(doc: DocRef): Promise<string | null>;
}

export class BrowserFileStore implements FileStore {
  private db = new Promise<IDBDatabase>((res, rej) => {
    const q = indexedDB.open('nandanam-demo-files', 1);
    q.onupgradeneeded = () => q.result.createObjectStore('files');
    q.onsuccess = () => res(q.result);
    q.onerror = () => rej(q.error);
  });
  async put(file: File, o: { id: string; folder: string; cat: DocCategory; date: string }): Promise<DocRef> {
    const db = await this.db;
    await new Promise<void>((res, rej) => { const t = db.transaction('files', 'readwrite'); t.objectStore('files').put(file, o.id); t.oncomplete = () => res(); t.onerror = () => rej(t.error); });
    return { id: o.id, name: file.name, cat: o.cat, date: o.date, size: file.size, mime: file.type || 'application/octet-stream', driveId: null };
  }
  async url(doc: DocRef): Promise<string | null> {
    const db = await this.db;
    const blob = await new Promise<Blob | undefined>((res, rej) => { const q = db.transaction('files').objectStore('files').get(doc.id); q.onsuccess = () => res(q.result); q.onerror = () => rej(q.error); });
    return blob ? URL.createObjectURL(blob) : null;
  }
}

export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
export const fmtSize = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Real mode: uploads go into the business folder in OneDrive (documents/, evidence/, receipts/). */
export class OneDriveFileStore implements FileStore {
  constructor(private g: Graph, private biz: BusinessLocation, private remote: OneDriveRemote) {}
  async put(file: File, o: { id: string; folder: string; cat: DocCategory; date: string }): Promise<DocRef> {
    const safe = file.name.replace(/[\\/:*?"<>|#%]+/g, '_').slice(-120) || 'file';
    const item = await uploadFile(this.g, this.biz, `${o.folder}/${o.id}-${safe}`, file, 'fail');
    return { id: o.id, name: file.name, cat: o.cat, date: o.date, size: file.size, mime: file.type || 'application/octet-stream', driveId: item.id };
  }
  /** A short-lived https link, so it opens on iPhone home-screen apps too (blob links do not). */
  async url(doc: DocRef): Promise<string | null> {
    if (!doc.driveId) return null;
    return downloadLink(this.g, this.biz.driveId, this.remote.fileMap[doc.driveId] ?? doc.driveId);
  }
}

/**
 * Open an uploaded file in a new tab. The tab is opened before waiting for the link, because Safari
 * blocks tabs opened after a wait.
 */
export async function openDoc(S: { files: FileStore; toast(m: string): void }, doc: DocRef, missing: string) {
  const w = window.open('', '_blank');
  try {
    const u = await S.files.url(doc);
    if (u) { if (w) w.location.href = u; else location.href = u; return; }
    w?.close();
    S.toast(missing);
  } catch (e) {
    w?.close();
    S.toast(e instanceof Error ? `Could not open the file: ${e.message}` : 'Could not open the file');
  }
}
